import { useState, useRef, useCallback } from "react";
import { disruptionAPI } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { Textarea } from "../ui/textarea";
import { Progress } from "../ui/progress";
import { toast } from "sonner";
import { Skull, Rocket, Target, ShieldAlert, Lightbulb, Heart, DollarSign,
  Flag, Swords, Cpu, Users, GraduationCap, Database, TrendingUp,
  Scale, Mic, MicOff, Check, ChevronDown, ChevronUp, Save, ChevronRight, CheckCircle2,
} from "lucide-react";

const CATEGORY_ICONS = {
  market: Target, weaknesses: ShieldAlert, innovation: Lightbulb,
  customer_exp: Heart, financial: DollarSign, brand: Flag, competitive: Swords,
  product_innovation: Lightbulb, ai_integration: Cpu, cx_engagement: Users,
  talent: GraduationCap, data: Database, scalability: TrendingUp, ethics: Scale,
};

const QuestionCard = ({ question, response, boardId, phase, onSaved, isAssigned }) => {
  const [text, setText] = useState(response?.response_text || "");
  const [notes, setNotes] = useState(response?.discussion_notes || "");
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(isAssigned);
  const [recording, setRecording] = useState(false);
  const recognitionRef = useRef(null);

  const save = async () => {
    setSaving(true);
    try {
      await disruptionAPI.submitResponse(boardId, {
        question_id: question.id,
        phase,
        response_text: text,
        discussion_notes: notes,
      });
      onSaved?.();
      toast.success("Saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const toggleVoice = () => {
    if (!("webkitSpeechRecognition" in window) && !("SpeechRecognition" in window)) {
      toast.error("Voice input not supported in this browser. Try Chrome.");
      return;
    }

    if (recording) {
      recognitionRef.current?.stop();
      setRecording(false);
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    let finalTranscript = text;

    recognition.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalTranscript += (finalTranscript ? " " : "") + event.results[i][0].transcript;
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      setText(finalTranscript + (interim ? " " + interim : ""));
    };

    recognition.onerror = () => {
      setRecording(false);
      toast.error("Voice recognition error");
    };

    recognition.onend = () => setRecording(false);

    recognitionRef.current = recognition;
    recognition.start();
    setRecording(true);
  };

  return (
    <div
      className={`bg-white rounded-xl border p-4 transition-colors ${
        isAssigned ? "border-indigo-200 shadow-sm" : "border-slate-200"
      } ${question.highlighted ? "ring-2 ring-amber-300 ring-offset-2" : ""}`}
      data-testid={`question-card-${question.id}`}
    >
      <div
        className="flex items-start justify-between cursor-pointer gap-3"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex-1">
          <p className="text-sm text-slate-800 leading-relaxed font-medium">{question.text}</p>
          {question.highlighted && (
            <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] mt-2">
              Provocative Prompt
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {response?.response_text && (
            <Check className="w-4 h-4 text-emerald-500" />
          )}
          {isAssigned && (
            <Badge className="bg-indigo-50 text-indigo-600 border-indigo-200 text-[10px]">Yours</Badge>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-300" /> : <ChevronDown className="w-4 h-4 text-slate-300" />}
        </div>
      </div>

      {expanded && (
        <div className="mt-4 space-y-3 animate-fade-in">
          <div className="relative">
            <Textarea
              data-testid={`response-${question.id}`}
              placeholder="Type your response or use voice input..."
              value={text}
              onChange={(e) => setText(e.target.value)}
              className={`min-h-[100px] bg-slate-50 border-slate-200 text-sm resize-none pr-12 ${
                recording ? "border-red-300 bg-red-50/30" : ""
              }`}
            />
            <Button
              variant="ghost"
              size="sm"
              data-testid={`voice-btn-${question.id}`}
              onClick={toggleVoice}
              className={`absolute top-2 right-2 h-8 w-8 p-0 ${
                recording ? "text-red-500 bg-red-50 animate-pulse" : "text-slate-400 hover:text-indigo-600"
              }`}
            >
              {recording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </Button>
          </div>

          {recording && (
            <div className="flex items-center gap-2 text-xs text-red-600">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              Listening... speak now
            </div>
          )}

          <Textarea
            placeholder="Discussion notes (added during presentation)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="min-h-[60px] bg-white border-slate-200 text-xs resize-none"
          />

          <div className="flex items-center justify-between">
            <span className="text-[10px] text-slate-400">
              {text.split(/\s+/).filter(Boolean).length} words
            </span>
            <Button
              data-testid={`save-response-${question.id}`}
              onClick={save}
              disabled={saving || !text.trim()}
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700 text-white h-8 text-xs"
            >
              <Save className="w-3 h-3 mr-1" />
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const QuestionPhaseView = ({ boardId, phase, questions, myPair, responses, onResponseSaved, isOwner, gameMode, onAdvancePhase, nextPhaseLabel }) => {
  const [activeCategory, setActiveCategory] = useState(0);

  if (!questions) return null;

  const categories = questions.categories || [];
  const PersonaIcon = phase === "phase1" ? Skull : Rocket;
  const personaColor = phase === "phase1" ? "text-red-600 bg-red-50" : "text-indigo-600 bg-indigo-50";

  const myQuestionIds = new Set([
    ...(myPair?.[`${phase}_questions`] || []),
  ]);

  const getResponse = (qId) => responses.find((r) => r.question_id === qId);

  const totalQuestions = categories.reduce((s, c) => s + c.questions.length, 0);
  const answeredQuestions = categories.reduce((s, c) =>
    s + c.questions.filter((q) => getResponse(q.id)?.response_text).length, 0
  );

  return (
    <div className="flex h-full" data-testid={`question-phase-${phase}`}>
      {/* Category Sidebar */}
      <aside className="w-56 bg-white border-r border-slate-200 hidden md:flex flex-col shrink-0">
        <div className={`px-4 py-3 border-b border-slate-100 flex items-center gap-2 ${personaColor.split(" ")[0]}`}>
          <PersonaIcon className="w-4 h-4" />
          <span className="text-xs font-bold uppercase tracking-wider">{questions.persona_label}</span>
        </div>
        <ScrollArea className="flex-1">
          <div className="py-1">
            {categories.map((cat, idx) => {
              const Icon = CATEGORY_ICONS[cat.id] || Target;
              const answered = cat.questions.filter((q) => getResponse(q.id)?.response_text).length;
              return (
                <div
                  key={cat.id}
                  onClick={() => setActiveCategory(idx)}
                  className={`flex items-center gap-2.5 px-4 py-2.5 cursor-pointer transition-colors ${
                    activeCategory === idx ? "bg-indigo-50 border-r-2 border-indigo-500" : "hover:bg-slate-50"
                  }`}
                >
                  <Icon className={`w-3.5 h-3.5 ${activeCategory === idx ? "text-indigo-600" : "text-slate-400"}`} />
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs font-medium truncate ${activeCategory === idx ? "text-indigo-700" : "text-slate-700"}`}>
                      {cat.name}
                    </div>
                    <div className="text-[10px] text-slate-400">{answered}/{cat.questions.length}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
        <div className="px-4 py-3 border-t border-slate-100">
          <div className="text-[10px] text-slate-400 mb-1">{answeredQuestions}/{totalQuestions} answered</div>
          <Progress value={(answeredQuestions / totalQuestions) * 100} className="h-1.5" />
          {answeredQuestions === totalQuestions && totalQuestions > 0 && onAdvancePhase && (
            <Button
              data-testid="sidebar-advance-btn"
              onClick={onAdvancePhase}
              size="sm"
              className="w-full mt-2 bg-indigo-600 hover:bg-indigo-700 text-white h-7 text-[10px]"
            >
              <ChevronRight className="w-3 h-3 mr-0.5" />{nextPhaseLabel || "Next Phase"}
            </Button>
          )}
        </div>
      </aside>

      {/* Questions */}
      <div className="flex-1 overflow-y-auto p-5">
        {categories[activeCategory] && (
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${personaColor}`}>
                {(() => { const I = CATEGORY_ICONS[categories[activeCategory].id] || Target; return <I className="w-5 h-5" />; })()}
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg text-slate-900">
                  {categories[activeCategory].name}
                </h3>
                <span className="text-xs text-slate-400">{categories[activeCategory].questions.length} questions</span>
              </div>
            </div>
            <div className="space-y-3">
              {categories[activeCategory].questions.map((q) => (
                <QuestionCard
                  key={q.id}
                  question={q}
                  response={getResponse(q.id)}
                  boardId={boardId}
                  phase={phase}
                  onSaved={onResponseSaved}
                  isAssigned={myQuestionIds.has(q.id) || myQuestionIds.size === 0}
                />
              ))}
            </div>

            {/* All questions answered — Continue banner */}
            {answeredQuestions === totalQuestions && totalQuestions > 0 && onAdvancePhase && (
              <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center animate-fade-in">
                <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                <h3 className="font-heading font-bold text-lg text-emerald-800 mb-1">
                  {phase === "phase1" ? "Phase 1 Complete!" : "Phase 2 Complete!"}
                </h3>
                <p className="text-sm text-emerald-600 mb-4">
                  You've answered all {totalQuestions} questions. {gameMode === "async" ? "You can continue to the next phase." : "Wait for the facilitator or continue now."}
                </p>
                <Button
                  data-testid="continue-next-phase-btn"
                  onClick={onAdvancePhase}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white h-10 px-6 font-medium active:scale-[0.98]"
                >
                  <ChevronRight className="w-4 h-4 mr-1.5" />
                  Continue to {nextPhaseLabel || "Next Phase"}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionPhaseView;
